// Unified listing detail (plan catalog-buy-unification, Track A / A2). The single
// Buy detail surface, read from the F2-extended `/catalog/listings/{id}` API.
// Replaces the two old detail screens — ProductScreen (direct-Supabase market
// detail) and ListingScreen (imported-catalog selector page) — which A4 deletes
// once A3 reroutes past them.
//
// It is ListingScreen's selector model (seedSelection + activeVariant best-match +
// ListingSelectors + ListingSpecList) PLUS ProductScreen's market block for the
// *active* variant: headline price + a price-basis label, the price-over-time
// chart (ItemPriceChart fed from variant.price_history — not a direct
// variant_valuations read), for-sale / held counts, and a "N comps tracked" +
// data-freshness strip (comps_tracked, last_price_refresh_at). All market fields
// are F2 additions and degrade to "Price unavailable" for an unlinked variant.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, ScrollView, Image } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../styles/ThemeProvider';
import { isUserAdmin } from '../../utils/userPermissions';
import {
  getCatalogListing,
  type CatalogListingDetail,
  type CatalogVariantDetail,
  type CatalogPriceBasis,
} from '../../lib/backendApi';
import { buildVariantMixAnnotation } from '../../lib/variantMixAnnotation';
import { shouldShowBrandLine } from '../../lib/listingTitle';
import ListingSelectors from '../../components/buy/ListingSelectors';
import ListingSpecList from '../../components/buy/ListingSpecList';
import ItemPriceChart from '../../components/ItemPriceChart';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { BuyStackParamList } from '../../types/navigation';

type Props = NativeStackScreenProps<BuyStackParamList, 'CatalogListing'>;

/** Exact market price for the active variant (the headline), or null → unavailable. */
function formatPrice(value: number | null): string | null {
  if (value == null) return null;
  return `$${Math.round(value).toLocaleString()}`;
}

// How the headline price was derived — mirrors the backend PriceBasis ladder
// (pricing_display_headline_used.price_basis). A short caption under the price so
// an operator can see whether they're looking at a curated anchor, a raw market
// median, or an upload-time estimate. Null basis (unpriced) and identity_collapsed
// (no price) → no caption.
const PRICE_BASIS_LABEL: Partial<Record<CatalogPriceBasis, string>> = {
  trusted_anchor: 'Trusted market anchor',
  market_median: 'Market median',
  estimate: 'Estimate',
};

// Human-friendly "X minutes/hours/days ago" — ported verbatim from ProductScreen
// so the freshness signal tracks the same buckets (<1h → minutes, <24h → hours,
// ≥24h → days). Returns null for missing / future / unparseable timestamps.
function formatTimeAgo(iso: string | null): string | null {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diffMs) || diffMs < 0) return null;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/** Seed the selector state from a variant's axis attributes (string values only). */
function seedSelection(variant: CatalogVariantDetail | undefined): Record<string, string> {
  if (!variant) return {};
  const seed: Record<string, string> = {};
  for (const [key, value] of Object.entries(variant.attributes ?? {})) {
    if (value != null && String(value).trim().length > 0) seed[key] = String(value);
  }
  return seed;
}

/**
 * The variant that best fits the user's explicit choices, oldest first in `choices`.
 * A newer choice outranks every older one together (weights 2^i), so the latest press
 * decides first and earlier presses only break ties; a remaining tie goes to the
 * earliest variant. Choices no variant combines are simply not matched (PR #687 review).
 */
function bestByChoices(
  variants: CatalogVariantDetail[],
  choices: [string, string][]
): CatalogVariantDetail | undefined {
  let best: CatalogVariantDetail | undefined;
  let bestScore = -1;
  for (const variant of variants) {
    let score = 0;
    choices.forEach(([axis, value], i) => {
      if (String(variant.attributes?.[axis] ?? '') === value) score += 2 ** i;
    });
    if (score > bestScore) {
      bestScore = score;
      best = variant;
    }
  }
  return best;
}

export default function UnifiedListingScreen({ route }: Props) {
  const theme = useTheme();
  const { listingTitle } = route.params;

  const [adminState, setAdminState] = useState<'pending' | 'allowed' | 'denied'>('pending');
  const [detail, setDetail] = useState<CatalogListingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // The opened variant's attributes, then the user's selector presses, oldest first, kept
  // apart from the resolved variant so a tie never discards an earlier choice (PR #687 review).
  const [choices, setChoices] = useState<[string, string][]>([]);
  const [variantId, setVariantId] = useState<string | null>(null);

  useEffect(() => {
    isUserAdmin().then((ok) => setAdminState(ok ? 'allowed' : 'denied'));
  }, []);

  useEffect(() => {
    if (adminState !== 'allowed') return;
    let cancelled = false;
    setLoading(true);
    // Reset per-listing state so navigating from a missing listing to an existing
    // one doesn't keep rendering the not-found view (or flash the prior detail).
    setNotFound(false);
    setDetail(null);
    getCatalogListing(route.params.listingId)
      .then((res) => {
        if (cancelled) return;
        if (!res) {
          setNotFound(true);
          return;
        }
        setDetail(res);
        // Opened from an owned device's "View in Buy" → preselect that variant's
        // config; a plain feed tap (no variantId) seeds from the first variant.
        const targetId = route.params.variantId;
        const seedVariant =
          (targetId && res.variants.find((v) => v.variant_id === targetId)) || res.variants[0];
        // The opened variant's own attributes start as the oldest (lowest-weight) choices:
        // defaults every later press outranks, seeded once per load, not after each press,
        // so the first press keeps the other preselected axes (PR #687 review).
        setChoices(Object.entries(seedSelection(seedVariant)));
        setVariantId(seedVariant?.variant_id ?? null);
      })
      .catch((err) => {
        if (!cancelled) console.error('[UnifiedListingScreen] load failed:', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [adminState, route.params.listingId, route.params.variantId]);

  // A press always lands on a variant that carries the pressed value: among those, the one
  // that best fits the user's explicit choices (newest first). The chips show that variant's
  // own attributes, so a pick no variant combines (White + 32 GB on a card whose 32 GB variant
  // is Black) is never shown as selected (PR #687 review).
  const onSelect = useCallback(
    (axisKey: string, value: string) => {
      const next: [string, string][] = [
        ...choices.filter(([axis]) => axis !== axisKey),
        [axisKey, value],
      ];
      const carrying = (detail?.variants ?? []).filter(
        (v) => String(v.attributes?.[axisKey] ?? '') === value
      );
      const target = bestByChoices(carrying, next);
      setChoices(next);
      if (target) setVariantId(target.variant_id);
    },
    [choices, detail]
  );

  const variant = useMemo(
    () =>
      detail
        ? (detail.variants.find((v) => v.variant_id === variantId) ?? detail.variants[0])
        : undefined,
    [detail, variantId]
  );
  const selected = useMemo(() => seedSelection(variant), [variant]);
  // Every variant's attributes, so a one-value selector only some variants carry stays a
  // tappable chip (PR #683 review).
  const variantAttributes = useMemo(
    () => detail?.variants.map((v) => (v.attributes ?? {}) as Record<string, unknown>),
    [detail]
  );

  const heroUrl = variant?.hero_image_url ?? detail?.listing.hero_image_url ?? null;
  const priceLabel = formatPrice(variant?.price_usd ?? null);
  const basisLabel = variant?.price_basis ? (PRICE_BASIS_LABEL[variant.price_basis] ?? null) : null;
  // Phase 3 S1: a collapsed variant spans several exact pricing identities, so its
  // variant-level history is not a valid series for any of them. Show no chart.
  const historyIsExact = variant?.price_basis !== 'identity_collapsed';
  // identity_collapsed is not a variant-mix caveat: with no price shown, drop the
  // annotation that described the hidden price.
  const variantMixAnnotation =
    variant && historyIsExact ? buildVariantMixAnnotation(variant) : null;

  // Freshness strip parts — "Last priced 2h ago · 7 comps tracked". An unpriced
  // variant has neither, so the strip renders a "—" placeholder (plan A2 negative).
  const freshnessParts = useMemo(() => {
    const parts: string[] = [];
    const lastSeen = formatTimeAgo(variant?.last_price_refresh_at ?? null);
    if (lastSeen) parts.push(`Last priced ${lastSeen}`);
    const comps = variant?.comps_tracked ?? null;
    if (comps != null && comps > 0) parts.push(`${comps} comp${comps === 1 ? '' : 's'} tracked`);
    return parts;
  }, [variant]);

  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: { flex: 1, backgroundColor: theme.colors.backgroundPrimary },
        centred: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
        },
        content: { padding: theme.spacing.lg, paddingBottom: 80 },
        hero: {
          width: '100%',
          aspectRatio: 1,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: theme.colors.backgroundElevated,
          marginBottom: theme.spacing.lg,
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
        },
        heroImage: { width: '100%', height: '100%' },
        heroPlaceholder: { ...theme.typography.h2, color: theme.colors.textTertiary },
        brand: { ...theme.typography.sm, color: theme.colors.textSecondary },
        title: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
          marginTop: theme.spacing.xxs,
        },
        // Raw catalog category · family — for spot-checking catalog data quality.
        catalogMeta: {
          ...theme.typography.sm,
          color: theme.colors.textTertiary,
          marginTop: theme.spacing.xxs,
        },
        price: {
          ...theme.typography.h3,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          marginTop: theme.spacing.sm,
        },
        basisLabel: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          marginTop: theme.spacing.xxs,
        },
        variantMixText: {
          ...theme.typography.xs,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.xxs,
          letterSpacing: 0,
        },
        newPrice: {
          ...theme.typography.sm,
          color: theme.colors.textTertiary,
          marginTop: theme.spacing.xxs,
        },
        // Freshness strip — recency + comp count for the active variant, under the
        // price block. Conveys data trust without taking layout space (from
        // ProductScreen's activity line).
        freshnessStrip: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          marginTop: theme.spacing.sm,
        },
        freshnessText: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          letterSpacing: 0,
        },
        // For-sale / held counts (from ProductScreen's hero stats row).
        statsRow: {
          flexDirection: 'row',
          gap: theme.spacing.xl,
          marginTop: theme.spacing.lg,
        },
        statBlock: { flex: 1 },
        statLabel: {
          ...theme.typography.xs,
          color: theme.colors.textTertiary,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
        },
        statValue: {
          ...theme.typography.valueSm,
          color: theme.colors.textPrimary,
          marginTop: theme.spacing.xxs,
        },
        message: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginTop: theme.spacing.md,
        },
      }),
    [theme]
  );

  if (adminState === 'pending' || (loading && adminState === 'allowed')) {
    return (
      <SafeAreaView style={[styles.container, styles.centred]} edges={['top']}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </SafeAreaView>
    );
  }

  if (adminState === 'denied') {
    return (
      <SafeAreaView style={[styles.container, styles.centred]} edges={['top']}>
        <Ionicons name="lock-closed-outline" size={32} color={theme.colors.textTertiary} />
        <Text style={styles.message}>The catalog is in admin-only preview right now.</Text>
      </SafeAreaView>
    );
  }

  if (notFound || !detail) {
    return (
      <SafeAreaView style={[styles.container, styles.centred]} edges={['top']}>
        <Text style={styles.message}>
          {listingTitle ? `“${listingTitle}” ` : 'This listing '}is not in the catalog.
        </Text>
      </SafeAreaView>
    );
  }

  const initial = (
    detail.listing.brand?.[0] ??
    detail.listing.listing_title[0] ??
    '?'
  ).toUpperCase();
  // `?.` after price_history too: a variant from a response that predates the F2
  // fields has no price_history key, and `variant?.price_history.length` would only
  // guard `variant`, then throw on `.length` of undefined. Degrade to "no chart".
  const hasHistory = (variant?.price_history?.length ?? 0) > 0;

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          {heroUrl ? (
            <Image source={{ uri: heroUrl }} style={styles.heroImage} resizeMode="cover" />
          ) : (
            <Text style={styles.heroPlaceholder}>{initial}</Text>
          )}
        </View>

        {shouldShowBrandLine(detail.listing.listing_title, detail.listing.brand) && (
          <Text style={styles.brand}>{detail.listing.brand}</Text>
        )}
        <Text style={styles.title}>{detail.listing.listing_title}</Text>

        {(!!detail.listing.category || !!detail.listing.family) && (
          <Text style={styles.catalogMeta}>
            {[detail.listing.category, detail.listing.family].filter(Boolean).join(' · ')}
          </Text>
        )}

        <Text style={styles.price}>{priceLabel ?? 'Price unavailable'}</Text>
        {!!basisLabel && <Text style={styles.basisLabel}>{basisLabel}</Text>}
        {!!variantMixAnnotation && (
          <Text style={styles.variantMixText}>{variantMixAnnotation}</Text>
        )}
        {detail.listing.new_price_usd != null && (
          <Text style={styles.newPrice}>
            ${Math.round(detail.listing.new_price_usd).toLocaleString()} new
          </Text>
        )}

        <View style={styles.freshnessStrip}>
          <Ionicons name="pulse-outline" size={12} color={theme.colors.textTertiary} />
          <Text style={styles.freshnessText}>
            {freshnessParts.length > 0 ? freshnessParts.join(' · ') : '—'}
          </Text>
        </View>

        <View style={styles.statsRow}>
          <View style={styles.statBlock}>
            <Text style={styles.statLabel}>For sale</Text>
            <Text style={styles.statValue}>{variant?.for_sale_count ?? 0}</Text>
          </View>
          <View style={styles.statBlock}>
            <Text style={styles.statLabel}>Held in Cache</Text>
            <Text style={styles.statValue}>{variant?.held_count ?? 0}</Text>
          </View>
        </View>

        <ListingSelectors
          selectors={detail.selectors}
          selected={selected}
          onSelect={onSelect}
          activeAttributes={variant?.attributes}
          variantAttributes={variantAttributes}
        />

        {variant && hasHistory && historyIsExact && (
          // Chart only when the active variant has a series — an unpriced variant
          // renders no chart (plan A2 negative). Keyed on variant_id so switching
          // a chip remounts cleanly (resets the period/tooltip state). Fed from
          // the catalog series, not a direct variant_valuations read.
          <ItemPriceChart
            key={variant.variant_id}
            variantId={variant.variant_id}
            series={variant.price_history}
            currentValuation={variant.price_usd ?? undefined}
          />
        )}

        <ListingSpecList specs={variant?.specs ?? {}} />
      </ScrollView>
    </SafeAreaView>
  );
}
